import { Skeleton } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";

export interface KPICardProps {
  label: string;
  value: string;
  // Fractional WoW delta. `null` means "not enough history to compare yet";
  // we render a neutral arrow rather than fake-zero.
  wowDelta?: number | null;
  loading?: boolean;
  hint?: string;
}

const formatDelta = (delta: number) => {
  const pct = Math.round(delta * 1000) / 10;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct}%`;
};

/**
 * Stripe-Dashboard-flavoured KPI tile. Spec:
 *
 * - 32px tabular value (font-semibold)
 * - 11px uppercase letter-spaced label
 * - Delta rendered as a ghost pill (`bg-success/danger-soft`) instead of
 *   raw colored text — reads cleaner against a card border.
 */
export const KPICard = ({ label, value, wowDelta, loading, hint }: KPICardProps) => {
  const direction =
    wowDelta === null || wowDelta === undefined
      ? "neutral"
      : wowDelta > 0
        ? "up"
        : wowDelta < 0
          ? "down"
          : "neutral";

  const Icon =
    direction === "up" ? ArrowUpRight : direction === "down" ? ArrowDownRight : ArrowRight;

  return (
    <div className="rounded-md border border-border bg-card p-5 transition-colors duration-120 ease-out">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">{label}</p>
      <div className="mt-3 flex items-end gap-3">
        {loading ? (
          <Skeleton className="h-8 w-28" />
        ) : (
          <span className="text-[32px] font-semibold leading-none tracking-tight tabular-nums text-foreground">
            {value}
          </span>
        )}
        {!loading && wowDelta !== undefined && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
              direction === "up" && "bg-success-soft text-success-foreground",
              direction === "down" && "bg-danger-soft text-danger-foreground",
              direction === "neutral" && "bg-surface text-muted-foreground",
            )}
          >
            <Icon className="size-3" />
            {wowDelta === null ? "—" : formatDelta(wowDelta)}
          </span>
        )}
      </div>
      {hint && <p className="mt-2 text-[12px] text-muted-foreground">{hint}</p>}
    </div>
  );
};
